/**
 * Progress Component
 *
 * A progress indicator showing completion percentage
 */

import React from 'react';

import { cn } from '@/lib/utils';

interface ProgressProps extends React.HTMLAttributes<HTMLDivElement> {
  value?: number;
  max?: number;
  size?: 'sm' | 'default' | 'lg';
  variant?: 'default' | 'success' | 'warning' | 'destructive';
}

const Progress: React.FC<ProgressProps> = ({
  className,
  value = 0,
  max = 100,
  size = 'default',
  variant = 'default',
  ...props
}) => {
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);

  return (
    <div
      {...props}
      className={cn(
        'relative overflow-hidden rounded-full bg-gray-20',
        size === 'sm' && 'h-1',
        size === 'default' && 'h-2',
        size === 'lg' && 'h-3',
        className
      )}
    >
      <div
        className={cn(
          'h-full transition-all duration-300 ease-in-out rounded-full',
          variant === 'default' && 'bg-brand-50',
          variant === 'success' && 'bg-success-50',
          variant === 'warning' && 'bg-warning-50',
          variant === 'destructive' && 'bg-destructive-50'
        )}
        style={{ width: `${percentage}%` }}
      />
    </div>
  );
};

export { Progress };
